import type { InitialEditorTarget } from '@canvas/model/ui';

import { element } from './util';

/** Arguments passed to each resolver when trying to identify what was clicked. */
type InitialEditorTargetResolverArgs = {
  targetElement: HTMLElement | SVGElement;
  clientX: number;
  clientY: number;
};

/** A function that checks whether a click hit a recognisable editor target. */
type InitialEditorTargetResolver = (
  args: InitialEditorTargetResolverArgs
) => InitialEditorTarget | undefined;

/** Config for a resolver that finds the closest ancestor matching a selector. */
type ClosestElementAttributeResolverConfig = {
  targetType: 'slideTarget';
  selector: string;
  targetIdAttribute: string;
};

/**
 * Creates a resolver that walks up the DOM looking for `selector`, then reads the target
 * ID from the specified attribute.
 */
const createClosestElementAttributeResolver =
  ({
    targetType,
    selector,
    targetIdAttribute,
  }: ClosestElementAttributeResolverConfig): InitialEditorTargetResolver =>
  ({ targetElement, clientX, clientY }) => {
    const editorTargetElement = targetElement.closest(selector);
    const targetId = editorTargetElement?.getAttribute(targetIdAttribute);

    if (!targetId) {
      return undefined;
    }

    return {
      targetType,
      targetId,
      clientX,
      clientY,
    };
  };

/**
 * Resolvers tried in order to identify which text box was clicked. Each checks the DOM
 * for a specific pattern (e.g. slide targets). To support new element types, add a
 * resolver here.
 */
const initialEditorTargetResolvers: InitialEditorTargetResolver[] = [
  createClosestElementAttributeResolver({
    targetType: 'slideTarget',
    selector: '[data-slide-node="slideTarget"][data-slide-id]',
    targetIdAttribute: 'data-slide-id',
  }),
];

/**
 * Checks whether the event target is inside a text box that the editor should activate
 * when it mounts. Returns the target info if found, `undefined` otherwise.
 */
export const getInitialEditorTarget = (
  target: EventTarget | null,
  clientX: number,
  clientY: number
): InitialEditorTarget | undefined => {
  const targetElement = element(target);
  if (!targetElement) {
    return undefined;
  }

  for (const resolver of initialEditorTargetResolvers) {
    const initialEditorTarget = resolver({ targetElement, clientX, clientY });
    if (initialEditorTarget) {
      return initialEditorTarget;
    }
  }

  return undefined;
};
